import ReactGA from 'react-ga4'
import { PageViewParams, InitParams, EventParams } from './types'

class Analytics {
  private isOptedOut = false
  private isInitialized = false
  private isTestEnvironment = false
  private currentUserId: string | null = null

  constructor() {
    this.isTestEnvironment = this.detectTestEnvironment()
  }

  private detectTestEnvironment(): boolean {
    const nodeEnv = process.env['NODE_ENV']
    const hostname = typeof window !== 'undefined' ? window.location?.hostname : ''
    const GAId = process.env['NX_GA_ID']
    
    const isLocalhost = hostname?.includes('localhost')
    if (isLocalhost && GAId) {
      console.log('🟡 Analytics: Localhost detected but GA_ID is configured - allowing analytics for development')
      return false
    }
    
    return (
      nodeEnv === 'test' ||
      hostname?.includes('test') ||
      isLocalhost ||
      typeof window === 'undefined' || 
      typeof document === 'undefined'   
    )
  }

  private isFakeUser(userId: string | null): boolean {
    return userId ? userId.endsWith('0be1bc24-162c-4acf-a19c-be72f33a666b') : false
  }

  private safeReactGACall(operation: string, callback: () => void): void {
    if (this.isTestEnvironment) {
      console.log(`Analytics: Skipping ${operation} in test environment`)
      return
    }

    try {
      callback()
    } catch (error) {
      console.warn(`Analytics: Error in ${operation}:`, error)
      // Don't throw the error, just log it
    }
  }

  public init({ userId }: InitParams) {
    const GAId = process.env['NX_GA_ID']
    
    console.log('🔍 Analytics.init() called with:', { userId, GAId: GAId ? 'SET' : 'NOT_SET' })
    
    this.currentUserId = userId || null

    // Block in test environment
    if (this.isTestEnvironment) {
      console.log('Analytics: Test environment detected. Analytics disabled.')
      this.isInitialized = false
      return
    }

    // Block if no GA ID
    if (!GAId) {
      console.log('Analytics: GA ID not found. Analytics disabled.')
      this.isInitialized = false
      return
    }

    // Check if user is fake user (only when userId is provided)
    if (userId && this.isFakeUser(userId)) {
      console.log('Analytics: Fake user detected. Analytics disabled.')
      this.isOptedOut = true
      this.isInitialized = false
      
      // Set GA disable flag for fake users
      try {
        (window as any)[`ga-disable-${GAId}`] = true;
      } catch (error) {
        console.warn('Analytics: Error setting GA disable flag:', error)
      }
      return
    }

    // Reset opt-out status for non-fake users or public pages
    this.isOptedOut = false

    // Initialize analytics for both public pages (userId: null) and authenticated non-fake users
    this.safeReactGACall('init', () => {
      const gaOptions = userId ? { userId } : {}
      ReactGA.initialize(GAId, {
        gaOptions,
      })
      this.isInitialized = true
      
      if (userId) {
        console.log('✅ Analytics: Initialized with userId:', userId)
      } else {
        console.log('✅ Analytics: Initialized for public pages (no userId)')
      }
    })
  }

  public reinitialize({ userId }: InitParams) {
    const GAId = process.env['NX_GA_ID']
    
    if (this.isTestEnvironment) {
      console.log('Analytics: Skipping reinitialize in test environment')
      return
    }
    
    if (!GAId) {
      return
    }

    this.currentUserId = userId || null

    // Check if user is fake user (only when userId is provided)
    if (userId && this.isFakeUser(userId)) {
      this.isOptedOut = true
      this.isInitialized = false
      console.log('Analytics: Fake user detected on reinitialize. Analytics disabled.')
      
      // Set GA disable flag for fake users
      try {
        (window as any)[`ga-disable-${GAId}`] = true;
      } catch (error) {
        console.warn('Analytics: Error setting GA disable flag:', error)
      }
      return
    }

    // Reset opt-out status for non-fake users
    this.isOptedOut = false

    // Reinitialize analytics with authenticated non-fake user
    this.safeReactGACall('reinitialize', () => {
      const gaOptions = userId ? { userId } : {}
      ReactGA.initialize(GAId, {
        gaOptions,
      })
      this.isInitialized = true
      console.log('Analytics: Reinitialized with userId:', userId)
    })
  }

  public pageView({ path, title, data, page, referrer }: PageViewParams) {
    // Only block in test environment or if opted out (fake user)
    if (this.isTestEnvironment || this.isOptedOut || !this.isInitialized) {
      if (this.isTestEnvironment) {
        console.log('Analytics pageView (test mode):', { path, title, page, referrer })
      } else if (this.isOptedOut) {
        console.log('Analytics pageView (opted out - fake user):', { path, title, page, referrer })
      }
      return
    }

    this.safeReactGACall('pageView', () => {
      ReactGA.send({ 
        hitType: 'pageview', 
        path: path, 
        page: page, 
        referrer: referrer,
        title, 
        ...data 
      })
    })
  }

  public event({ category, action, value, label }: EventParams) {
    // Only block in test environment or if opted out (fake user)
    if (this.isTestEnvironment || this.isOptedOut || !this.isInitialized) {
      if (this.isTestEnvironment) {
        console.log('Analytics event (test mode):', { category, action, value, label })
      } else if (this.isOptedOut) {
        console.log('Analytics event (opted out - fake user):', { category, action, value, label })
      }
      return
    }

    this.safeReactGACall('event', () => {
      ReactGA.event({ 
        category, 
        action, 
        value,
        label 
      })
    })
  }
}

export const analytics = new Analytics()